import React, { useState } from 'react';

interface ShareComposerProps {
  shareUrl: string;
  shareText: string;
  evidenceUrl?: string | null;
  compact?: boolean;
  shareVariants?: { label: string; text: string }[];
}

export default function ShareComposer({ shareUrl, shareText, evidenceUrl, compact = false, shareVariants }: ShareComposerProps) {
  const [copyState, setCopyState] = useState<string>('idle');

  const handleCopy = async (variantLabel: string, textToCopy: string) => {
    try {
      await navigator.clipboard.writeText(`${textToCopy}\n${shareUrl}`.trim());
      setCopyState(variantLabel);
    } catch {
      setCopyState(`error:${variantLabel}`);
    } finally {
      window.setTimeout(() => setCopyState('idle'), 2000);
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ text: shareText, url: shareUrl });
    } catch {
      // User canceled or platform rejected the share sheet.
    }
  };

  const buttonClass = compact
    ? 'rounded-lg border border-cream-300 bg-cream-100 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-navy-700 transition hover:border-gold-500 hover:text-gold-600 dark:border-navy-600 dark:bg-navy-800/60 dark:text-cream-200 dark:hover:text-gold-400'
    : 'rounded-lg border border-cream-300 bg-cream-100 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-navy-700 transition hover:border-gold-500 hover:text-gold-600 dark:border-navy-600 dark:bg-navy-800/60 dark:text-cream-200 dark:hover:text-gold-400';

  const variants = shareVariants && shareVariants.length > 0
    ? shareVariants
    : [{ label: 'Copy receipt', text: shareText }];

  return (
    <div className="flex flex-wrap items-center gap-2">
      {variants.map((variant) => {
        const copied = copyState === variant.label;
        const failed = copyState === `error:${variant.label}`;
        return (
          <button key={variant.label} onClick={() => handleCopy(variant.label, variant.text)} className={buttonClass}>
            {copied ? 'Copied' : failed ? 'Copy failed' : variant.label}
          </button>
        );
      })}
      {navigator.share && (
        <button onClick={handleNativeShare} className={buttonClass}>
          Share
        </button>
      )}
      {evidenceUrl && (
        <a href={evidenceUrl} target="_blank" rel="noopener noreferrer" className={buttonClass}>
          Open evidence
        </a>
      )}
    </div>
  );
}
